/**
 * usePreparedDropsUndo — status + one step back on the prepared-drops list.
 * For Media Devoted | Created By Ciro Autiero
 *
 * Mirrors useBankUndo, which cannot be reused as-is: its paths are hard-coded
 * to /voluum/<resource>/undo. The status drives the button's visibility, so a
 * 403 (viewer-only role) simply renders nothing rather than needing permission
 * plumbing in the component.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '@/services/api';
import { smswiserCampaignWorkspaceKeys as keys } from '@/hooks/smswiser-campaign-workspace/keys';

export type PreparedDropsUndoOp =
  | 'delete'
  | 'update'
  | 'create'
  | 'stage-draft'
  | 'send'
  | 'remote-edit'
  /** A killed drop re-sent as a new row: the undo stops it and removes the row. */
  | 'resend';

export interface PreparedDropsUndoStatus {
  available: boolean;
  op?: PreparedDropsUndoOp;
  dropCount: number;
  countryKeys: string[];
  createdAt?: string;
}

export interface PreparedDropsUndoResult {
  restored: number;
  skipped: number;
  skippedMissingCampaign: number;
  countryKeys: string[];
}

export const PREPARED_DROPS_UNDO_KEY = ['prepared-drops-undo'] as const;

const EMPTY: PreparedDropsUndoStatus = {
  available: false,
  dropCount: 0,
  countryKeys: [],
};

export function usePreparedDropsUndo() {
  const qc = useQueryClient();

  const status = useQuery({
    queryKey: PREPARED_DROPS_UNDO_KEY,
    queryFn: async () =>
      (
        await api.get<{ data: PreparedDropsUndoStatus }>(
          '/smswiser/prepared-drops/undo',
        )
      ).data.data,
    // The offer expires server-side; poll so the button disappears on its own
    // rather than lingering until the next click.
    refetchInterval: 60_000,
    retry: false,
  });

  const undo = useMutation({
    mutationFn: async () =>
      (
        await api.post<{ data: PreparedDropsUndoResult }>(
          '/smswiser/prepared-drops/undo',
        )
      ).data.data,
    onSuccess: (result) => {
      // Prefix invalidation covers every country and both pages — the pinned
      // list and a campaign's Prepared tab read the same key family.
      for (const countryKey of result.countryKeys) {
        qc.invalidateQueries({
          queryKey: keys.preparedDropsByCountryPrefix(countryKey),
        });
      }
      qc.invalidateQueries({ queryKey: PREPARED_DROPS_UNDO_KEY });
    },
  });

  return { status: status.data ?? EMPTY, undo };
}
